import { TooltipPortal } from '@radix-ui/react-tooltip';
import { ArrowRightUp } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useDelayedState } from 'src/utils/useDelayedState';
import { LinkExternal } from './LinkExternal';
import { TooltipContent, TooltipRoot, TooltipTrigger } from './Tooltip';

export function SidebarExternalLink({
  icon: Icon,
  label,
  href,
  className,
  expanded = false,
  iconOnExpanded = true,
  transitionDelay = 100,
}: {
  icon?: React.FC<{ className?: string }>;
  label: ReactNode;
  href: string;
  className?: string;
  expanded: boolean;
  iconOnExpanded?: boolean;
  transitionDelay?: number;
}) {
  const [isHidden, setIsHidden] = useDelayedState(!expanded);

  useEffect(() => {
    if (expanded) {
      setIsHidden(false);
    } else {
      setIsHidden(true, transitionDelay); // Delay hiding the element until after the collapse animation
    }
  }, [expanded, setIsHidden]);

  return (
    <TooltipRoot open={expanded ? false : undefined}>
      <LinkExternal
        to={href}
        className={clsx(
          'group rounded-sm h-48 w-full font-medium no-underline transition-colors hover:bg-paper-4 data-[status=active]:bg-paper-6',
          {
            hidden: isHidden && !Icon,
          },
          className
        )}
        componentLocation={ComponentLocations.MENU.SIDE_MENU}
      >
        <TooltipTrigger asChild>
          <span className="flex h-48 w-full items-center gap-4 px-8">
            {Icon && (
              <Icon
                className={clsx('!h-20 shrink-0 transition-opacity', {
                  'opacity-0': !expanded && iconOnExpanded,
                  'opacity-100': isHidden || !iconOnExpanded,
                  hidden: !isHidden && !iconOnExpanded,
                })}
              />
            )}
            <Typography
              variant="interface-md"
              as="span"
              className={clsx(
                'h-16 w-full overflow-x-clip font-regular whitespace-nowrap transition-all group-data-[status=active]:font-medium',
                {
                  'opacity-0': !expanded,
                  'opacity-100': expanded,
                  'pl-8': !Icon || (expanded && !iconOnExpanded),
                }
              )}
            >
              {label}
            </Typography>
            <ArrowRightUp
              className={clsx('!h-20 shrink-0 transition-all', {
                'opacity-0': !expanded,
                'opacity-100': expanded,
              })}
            />
          </span>
        </TooltipTrigger>
      </LinkExternal>
      <TooltipPortal>
        <TooltipContent side="right" sideOffset={16}>
          {label}
        </TooltipContent>
      </TooltipPortal>
    </TooltipRoot>
  );
}
